import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import SecondaryLoader from '../loaders/SecondaryLoader';

export default function OurReserveHero() {
  const theme = useTheme();
  const navigate = useNavigate();
  const headerDesign =
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-7-scaled.png';
  const accent = '#b0a990';

  // Loader state for redirect
  const [loading, setLoading] = useState(false);

  // Two reserve images
  const reserveImages = [
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/olive-landscape.png',
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/olive-tree-contrast-scaled.jpg',
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/olive-press-square.png',
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/woman-picking-olives.jpg',
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/olive-farm-walk.jpg',
  ];

  // Slide show state and animation controls
  const [currentImage, setCurrentImage] = useState(0);
  const controls = useAnimation();

  useEffect(() => {
    const timer = setTimeout(() => setCurrentImage(i => (i + 1) % reserveImages.length), 5000);
    return () => clearTimeout(timer);
  }, [currentImage]);

  const { ref, inView } = useInView({ once: true, threshold: 0.3 });
  useEffect(() => {
    if (inView) controls.start('visible');
  }, [controls, inView]);

  const imageMotion = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 1 } },
    exit: { opacity: 0, transition: { duration: 1 } },
  };

  const textVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { delay: 0.3, duration: 0.6 } },
  };

  const handleClick = () => {
    setLoading(true);
    // Show loader for at least 2 seconds before navigating
    setTimeout(() => {
      navigate('/products');
    }, 2000);
  };

  return (
    <>
      {/* Secondary loader overlay */}
      <AnimatePresence>
        {loading && <SecondaryLoader key="secondary-loader-reserve" />}
      </AnimatePresence>

      <Box
        id="reserve"
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: 'common.black',
          color: 'common.white',
          py: { xs: 4, md: 4 },
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: 'center',
            gap: { xs: 2, md: 4 },
          }}
        >
          {/* TEXT SECTION */}
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={controls}
            style={{ flex: 1 }}
          >
            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mb: 4,
                display: 'block',
                ml: { xs: '-16px', md: '-24px' },
              }}
            />

            <Box
              sx={{ width: 60, height: 4, bgcolor: 'secondary.main', mb: 2, borderRadius: 2 }}
            />

            <Typography
              variant="h3"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '1px',
                mb: 1.5,
              }}
            >
              Our Reserve
            </Typography>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: '#FFFFFF',
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >
              Nestled on the foothills of the Atlas Mountains near Marrakech,
              our centuries-old olive trees benefit from cool mountain breezes and
              mineral-rich soils. Harvested by hand at dawn and cold-pressed by
              noon, this small-batch oil reveals vibrant green notes of fresh
              herbs, a hint of wild almond, and a gentle peppery finish that
              captures the essence of its lofty origin.
            </Typography>

            <Button
              component={motion.div}
              whileHover={{ x: 5 }}
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleClick}
              sx={{
                backgroundColor: 'transparent',
                color: '#fff',
                border: `1px solid ${accent}`,
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 300,
                textTransform: 'none',
                px: 3,
                py: 1,
                fontSize: '0.75rem',
                borderRadius: 1,
                transition: 'all 0.3s ease',
                '&:hover': { backgroundColor: accent, color: '#000' },
              }}
            >
              Discover Our Olive Oil
            </Button>

            {/* bottom headerDesign image */}
            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mt: 4,
                display: 'block',
                ml: 'auto',
                mr: { xs: '-16px', md: '-24px' },
              }}
            />
          </motion.div>

          {/* IMAGE (auto-fade every 5s) */}
          <Box
            sx={{
              flex: '0 0 auto',
              width: '50%',
              margin: '0 auto',
              position: 'relative',
              height: 0,
              paddingBottom: '40%',
              clipPath: 'polygon(10% 0%, 100% 0, 90% 100%, 0% 100%)',
              overflow: 'hidden',
            }}
          >
            <AnimatePresence exitBeforeEnter>
              <Box
                key={reserveImages[currentImage]}
                component={motion.img}
                src={reserveImages[currentImage]}
                alt="Olive landscape"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={imageMotion}
                loading="lazy"
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </AnimatePresence>
            {/* Gradient overlay */}
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom right, rgba(0,0,0,0.3), rgba(0,0,0,0.6))', zIndex: 1 }} />
          </Box>
        </Container>
      </Box>
    </>
  );
}
